import { motion } from "framer-motion";
import { Target, Eye, ArrowUpRight } from "@phosphor-icons/react";
import { IMG, VALUES } from "../data/content";
import { Reveal } from "../lib/motion";
import { Btn } from "../components/Btn";
import SectionHeading from "../components/SectionHeading";
import ContactCTA from "../components/ContactCTA";

const TEAM = [
  { name: "Projectdirecteur", role: "Directievoering & Strategie" },
  { name: "Senior Projectleider", role: "Renovatie & Woningbouw" },
  { name: "Adviseur Werktuigbouwkunde", role: "Installaties & Energie" },
  { name: "Projectcoördinator", role: "Zorgvastgoed & Onderhoud" },
];

function PageHeader({ eyebrow, title, intro }) {
  return (
    <section className="pt-40 pb-16 md:pb-20">
      <div className="container-ballk max-w-4xl">
        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="eyebrow mb-5">{eyebrow}</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
          className="font-heading font-bold text-ballk-indigo text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">{title}</motion.h1>
        {intro && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-6 text-ballk-steel text-lg max-w-2xl leading-relaxed">{intro}</motion.p>}
      </div>
    </section>
  );
}

export default function Over() {
  return (
    <div data-testid="over-page">
      <PageHeader eyebrow="Over ons"
        title="Een betrouwbare partner in bouw, techniek en vastgoed."
        intro="BALLK Project is een onafhankelijk projectmanagementbureau. Wij zorgen voor overzicht, vertrouwen en resultaat bij complexe opgaven binnen de vastgoedsector." />

      <section className="pb-8">
        <div className="container-ballk">
          <Reveal>
            <div className="overflow-hidden rounded-[24px] diag-img">
              <img src={IMG.hero} alt="Modern gebouw" className="w-full h-[380px] md:h-[520px] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24">
        <div className="container-ballk grid md:grid-cols-2 gap-8">
          {[{ icon: Target, t: "Missie", d: "Wij begeleiden opdrachtgevers bij complexe bouw-, onderhouds- en installatieprojecten — met overzicht en vakmanschap, van eerste idee tot oplevering. Altijd met de mens en het gebouw centraal." },
            { icon: Eye, t: "Visie", d: "Bouwen en verduurzamen doe je samen. Wij geloven in transparante samenwerking, korte lijnen en technische kwaliteit die vastgoed toekomstbestendig maakt." }].map((b) => (
            <Reveal key={b.t}>
              <div className="h-full bg-white rounded-2xl p-10 border border-ballk-light shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
                <div className="h-14 w-14 rounded-xl bg-ballk-purple/10 flex items-center justify-center text-ballk-purple"><b.icon size={28} weight="duotone" /></div>
                <h3 className="mt-6 font-heading font-bold text-2xl text-ballk-indigo">{b.t}</h3>
                <p className="mt-4 text-ballk-steel leading-relaxed">{b.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 bg-[#FAFAFB] border-y border-ballk-light">
        <div className="container-ballk">
          <SectionHeading eyebrow="Kernwaarden" title="Waar wij voor staan." align="center" />
          <div className="mt-14 flex flex-wrap justify-center gap-4">
            {VALUES.map((v, i) => (
              <Reveal key={v} delay={i * 0.05}>
                <span className="inline-flex items-center gap-2 bg-white border border-ballk-light rounded-full px-6 py-3 font-heading font-semibold text-ballk-indigo">
                  <span className="h-2 w-2 rounded-full bg-ballk-purple" /> {v}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="container-ballk">
          <SectionHeading eyebrow="Team" title="Betrokken specialisten met korte lijnen."
            intro="Een compact en ervaren team dat u persoonlijk begeleidt van start tot oplevering." />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {TEAM.map((m, i) => (
              <Reveal key={m.role} delay={i * 0.06}>
                <div data-testid={`team-member-${i}`} className="group">
                  <div className="relative overflow-hidden rounded-2xl aspect-[3/4] bg-ballk-light">
                    <img src={[IMG.meeting, IMG.construction, IMG.hvac, IMG.apartment][i]} alt={m.name}
                      className="h-full w-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105" />
                  </div>
                  <h3 className="mt-5 font-heading font-semibold text-lg text-ballk-indigo">{m.name}</h3>
                  <p className="text-sm text-ballk-purple">{m.role}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}><Btn to="/contact" className="mt-12">Kom in gesprek <ArrowUpRight size={18} weight="bold" /></Btn></Reveal>
        </div>
      </section>

      <ContactCTA />
    </div>
  );
}
